/**
 * Centralized user-notification abstraction.
 *
 * Replaces raw `alert()` calls and the duplicated ad-hoc `showToast` helpers
 * scattered across pages with one wrapper over Syncfusion's `ToastUtility`
 * (already the app's de-facto toast mechanism). Using `notify.*` keeps the
 * toast type/timeout conventions consistent and gives a single seam to swap
 * the toast implementation later.
 *
 * Usage: `import { notify } from "utility/notify";` then
 * `notify.error("Failed to save")`.
 */
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import type {
  ToastClickEventArgs,
  ToastModel,
} from "@syncfusion/ej2-react-notifications";

type ToastType = "Success" | "Error" | "Info" | "Warning";

/**
 * What `ToastUtility.show(message, type, timeout)` puts on a toast for each type.
 *
 * Needed because the two calls below are NOT the same call. Handed a string, `ToastUtility`
 * derives the class and the icon from `type` itself; handed a `ToastModel` — the only shape
 * that can carry a click handler — it passes the model through untouched, so a clickable toast
 * that did not restate these would render unstyled and stop looking like an error at all.
 *
 * ⚠️ `Info` MAPS TO NOTHING, AND THAT IS FAITHFUL RATHER THAN MISSING. Syncfusion switches on
 * `Information`, not `Info` (`ej2-notifications/src/toast/toast.js`), so every `notify.info`
 * toast in the app today already renders with no class and no icon. Parity is the rule here: a
 * toast must not change its appearance merely because it became clickable. Correcting the
 * spelling would restyle every info toast in the app at once and is a separate change.
 */
const TOAST_LOOK: Record<ToastType, { cssClass: string; icon: string }> = {
  Success: { cssClass: "e-toast-success", icon: "e-toast-success-icon" },
  Error: { cssClass: "e-toast-danger", icon: "e-toast-error-icon" },
  Warning: { cssClass: "e-toast-warning", icon: "e-toast-warning-icon" },
  Info: { cssClass: "", icon: "" },
};

/**
 * Marks a toast the user is meant to press. Carries the cursor and the hover treatment
 * (`App.css`); without it a clickable toast looks exactly like one that ignores clicks.
 */
const CLICKABLE = "csm-toast-clickable";

function show(
  message: string,
  type: ToastType,
  timeout: number,
  onClick?: () => void
): void {
  // The string form is kept for every ordinary toast: it is the call the four assertions in
  // notify.test.ts pin, and a model would only restate what Syncfusion already derives.
  if (!onClick) {
    ToastUtility.show(message, type, timeout);
    return;
  }

  const { cssClass, icon } = TOAST_LOOK[type];
  const model: ToastModel = {
    content: message,
    cssClass: `${cssClass} ${CLICKABLE}`.trim(),
    icon,
    timeOut: timeout,
    click: (args: ToastClickEventArgs) => {
      // Dismiss on the same press that acts on it. Syncfusion destroys the toast after this
      // handler returns when `clickToClose` is set; leaving it up would put a dead red bubble
      // over whatever the press just opened.
      args.clickToClose = true;
      onClick();
    },
  };

  ToastUtility.show(model);
}

export const notify = {
  success: (message: string, timeout = 2000, onClick?: () => void) =>
    show(message, "Success", timeout, onClick),
  error: (message: string, timeout = 4000, onClick?: () => void) =>
    show(message, "Error", timeout, onClick),
  info: (message: string, timeout = 2000, onClick?: () => void) =>
    show(message, "Info", timeout, onClick),
  warning: (message: string, timeout = 3000, onClick?: () => void) =>
    show(message, "Warning", timeout, onClick),
};

export default notify;
